import { Chart } from 'chart.js/auto';
import classNames from 'classnames';
import { type ComponentRef, useEffect, useRef, useState } from 'react';
import { getUsageForTenant } from '../../../sources/dashboard';
import translations from '../../../translations';
import type {
    ChartDataPoint,
    ChartLine,
    FlowUsageDashboardResponse,
    TimeFilters,
    UsageDashboardBucket,
} from '../../../types/dashboard';
import {
    convertDateFromUnixTime,
    convertDateToUnixTime,
    formatChartDate,
    getDashboardLineChartOptions,
    getFromDate,
    getSecondsToIncrement,
    insightDateFilterItems,
} from '../../../utils/dashboard';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { View } from '../InsightsDashboard';
import InsightsDateFilter from '../InsightsDateFilter';
import { useInsights } from '../InsightsProvider';

const getChartData = (
    flowUsage: FlowUsageDashboardResponse[],
    toDate: Date,
    dateRange: TimeFilters,
) => {
    const chartLine: ChartLine[] = [];
    const usageLineData: ChartDataPoint[] = [];

    const fromDate = getFromDate(dateRange, toDate);

    for (
        let time = convertDateToUnixTime(fromDate);
        time <= convertDateToUnixTime(toDate);
        time += getSecondsToIncrement(dateRange, convertDateFromUnixTime(time))
    ) {
        const bucketsInRange: UsageDashboardBucket[] = [];

        flowUsage.forEach((usage) => {
            const dataInTimeRange = usage.flowLaunchData.filter((usageData) => {
                const dataUnix = convertDateToUnixTime(usageData.bucket);
                return (
                    dataUnix > time &&
                    dataUnix <
                        time + getSecondsToIncrement(dateRange, convertDateFromUnixTime(time))
                );
            });

            bucketsInRange.push(...dataInTimeRange);
        });

        const usageForTimeBucket = bucketsInRange.reduce(
            (accumulator, currentValue) => accumulator + currentValue.counter,
            0,
        );

        usageLineData.push({
            x: formatChartDate(convertDateFromUnixTime(time), dateRange),
            y: usageForTimeBucket,
        });
    }

    chartLine.push({
        data: usageLineData,
        label: 'Usage',
        tension: 0.3,
    });

    return chartLine;
};

interface Props {
    setCurrentView: (view: View) => void;
}

const UsageSummary = ({ setCurrentView }: Props) => {
    const [isHovering, setIsHovering] = useState(false);
    const [usageLine, setUsageLine] = useState<ChartLine[]>([]);
    const dispatchNotification = useNotificationDispatch();

    const canvasRef = useRef<ComponentRef<'canvas'>>(null);

    const { usageDateFilter, setUsageDateFilter } = useInsights();

    useEffect(() => {
        const toDate = new Date();

        getUsageForTenant(usageDateFilter, toDate)
            .then((response) => {
                setUsageLine(getChartData(response, toDate, usageDateFilter));
            })
            .catch((error) => {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            });
    }, [usageDateFilter, dispatchNotification]);

    useEffect(() => {
        const context = canvasRef.current?.getContext('2d');

        if (!context) {
            return;
        }

        const chart = new Chart(context, {
            type: 'line',
            data: { datasets: usageLine },
            options: getDashboardLineChartOptions(),
        });

        // Cleanup the chart to prevent it from wildly animating when switching away and then back to Dashboard tab.
        return () => {
            chart?.destroy();
        };
    }, [usageLine]);

    const tileClasses = classNames(
        'insights-chart-wrapper',
        'insights-tile',
        isHovering ? 'insights-tile-hover' : '',
    );

    return (
        // biome-ignore lint/a11y/useSemanticElements: Requires dedicated refactor to use correct element
        <div
            className={tileClasses}
            onClick={() => setCurrentView(View.usage)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    setCurrentView(View.usage);
                }
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            role="button"
            tabIndex={0}
            data-testid="usage-dashboard-tile"
        >
            <div className="title-bar insight-tile-header">
                <h2>{translations.DASHBOARD_usage_header}</h2>
                <InsightsDateFilter
                    selectedDate={usageDateFilter}
                    setSelectedDate={setUsageDateFilter}
                    dateFilterItems={insightDateFilterItems}
                    setParentHovering={setIsHovering}
                />
            </div>
            <div className="insights-chart">
                <canvas ref={canvasRef} data-testid="usage-chart-canvas" />
            </div>
        </div>
    );
};

export default UsageSummary;
